import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AdminShell } from "@/components/layouts/admin-shell";
import { ProtectedRoute } from "@/features/auth/components/protected-route";

const LoginPage = lazy(() => import("@/features/auth/components/login-page"));
const PasswordSetupPage = lazy(
  () => import("@/features/auth/components/password-setup-page"),
);
const DashboardPage = lazy(
  () => import("@/features/dashboard/components/dashboard-page"),
);
const LpsPage = lazy(() => import("@/features/lps/components/lps-page"));
const LpCreatePage = lazy(
  () => import("@/features/lps/components/lp-create-page"),
);
const LpEditPage = lazy(
  () => import("@/features/lps/components/lp-edit-page"),
);
const MediaPage = lazy(() => import("@/features/media/components/media-page"));
const MaterialsListPage = lazy(
  () => import("@/features/materials/components/materials-list-page"),
);
const MaterialDetailPage = lazy(
  () => import("@/features/materials/components/material-detail-page"),
);
const MaterialEditPage = lazy(
  () => import("@/features/materials/components/material-edit-page"),
);
const NovaSitesListPage = lazy(
  () => import("@/features/nova-sites/components/nova-sites-list-page"),
);
const NovaSiteCreatePage = lazy(
  () => import("@/features/nova-sites/components/nova-site-create-page"),
);
const NovaSiteDetailPage = lazy(
  () => import("@/features/nova-sites/components/nova-site-detail-page"),
);
const NovaSiteEditPage = lazy(
  () => import("@/features/nova-sites/components/nova-site-edit-page"),
);
const CtasPage = lazy(() => import("@/features/ctas/components/ctas-page"));
const CtaCreatePage = lazy(
  () => import("@/features/ctas/components/cta-create-page"),
);
const CtaDetailPage = lazy(
  () => import("@/features/ctas/components/cta-detail-page"),
);
const CtaEditPage = lazy(
  () => import("@/features/ctas/components/cta-edit-page"),
);
const CushionPerformanceReportPage = lazy(
  () => import("@/features/reports/components/cushion-performance-report-page"),
);
const NovaSiteAccessPage = lazy(
  () => import("@/features/event-logs/components/nova-site-access-page"),
);
const AccessPage = lazy(
  () => import("@/features/event-logs/components/access-page"),
);
const AccessLogExportsPage = lazy(
  () => import("@/features/event-logs/components/access-log-exports-page"),
);
const CtaAccessPage = lazy(
  () => import("@/features/event-logs/components/cta-access-page"),
);
const CtaActionPage = lazy(
  () => import("@/features/event-logs/components/cta-action-page"),
);
const ResultsPage = lazy(
  () => import("@/features/event-logs/components/results-page"),
);
const CtaPostbackPage = lazy(
  () => import("@/features/event-logs/components/cta-postback-page"),
);
const PostbacksPage = lazy(
  () => import("@/features/event-logs/components/postbacks-page"),
);
const DirectAccessPage = lazy(
  () => import("@/features/event-logs/components/direct-access-page"),
);
const DirectPostbackPage = lazy(
  () => import("@/features/event-logs/components/direct-postback-page"),
);
const OfferDetailPage = lazy(
  () => import("@/features/offers/components/offer-detail-page"),
);
const OfferCreateWizard = lazy(
  () => import("@/features/offers/components/offer-create-wizard"),
);
const OfferEditPage = lazy(
  () => import("@/features/offers/components/offer-edit-page"),
);
const OffersListPage = lazy(
  () => import("@/features/offers/components/offers-list-page"),
);
const SettingsPage = lazy(
  () => import("@/features/settings/components/settings-page"),
);
const SparkSyncPage = lazy(
  () => import("@/features/spark-sync/components/spark-sync-page"),
);
const UsersPage = lazy(
  () => import("@/features/users/components/users-page"),
);
const PartnershipRequestsPage = lazy(() =>
  import("@/features/placeholders/components/unimplemented-pages").then((m) => ({
    default: m.PartnershipRequestsPage,
  })),
);
const InvoicesPage = lazy(() =>
  import("@/features/placeholders/components/unimplemented-pages").then((m) => ({
    default: m.InvoicesPage,
  })),
);
const PaymentsPage = lazy(() =>
  import("@/features/placeholders/components/unimplemented-pages").then((m) => ({
    default: m.PaymentsPage,
  })),
);
const MailDeliveriesPage = lazy(() =>
  import("@/features/placeholders/components/unimplemented-pages").then((m) => ({
    default: m.MailDeliveriesPage,
  })),
);
const StopReservationsPage = lazy(() =>
  import("@/features/placeholders/components/unimplemented-pages").then((m) => ({
    default: m.StopReservationsPage,
  })),
);

function PageFallback() {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
      読み込み中...
    </div>
  );
}

function lazyRoute(node: ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={lazyRoute(<LoginPage />)} />
      <Route
        path="/password-setup"
        element={lazyRoute(<PasswordSetupPage />)}
      />
      <Route
        element={
          <ProtectedRoute>
            <AdminShell />
          </ProtectedRoute>
        }
      >
        <Route index element={lazyRoute(<DashboardPage />)} />
        <Route path="/lps" element={lazyRoute(<LpsPage />)} />
        <Route path="/lps/new" element={lazyRoute(<LpCreatePage />)} />
        <Route path="/lps/:id/edit" element={lazyRoute(<LpEditPage />)} />
        <Route path="/nova-sites" element={lazyRoute(<NovaSitesListPage />)} />
        <Route
          path="/nova-sites/new"
          element={lazyRoute(<NovaSiteCreatePage />)}
        />
        <Route
          path="/nova-sites/:id"
          element={lazyRoute(<NovaSiteDetailPage />)}
        />
        <Route
          path="/nova-sites/:id/edit"
          element={lazyRoute(<NovaSiteEditPage />)}
        />
        <Route path="/ctas" element={lazyRoute(<CtasPage />)} />
        <Route path="/ctas/new" element={lazyRoute(<CtaCreatePage />)} />
        <Route path="/ctas/:id" element={lazyRoute(<CtaDetailPage />)} />
        <Route path="/ctas/:id/edit" element={lazyRoute(<CtaEditPage />)} />
        <Route
          path="/reports"
          element={<Navigate to="/reports/cushion-performance" replace />}
        />
        <Route
          path="/reports/cushion-performance"
          element={lazyRoute(<CushionPerformanceReportPage />)}
        />
        <Route path="/access" element={lazyRoute(<AccessPage />)} />
        <Route
          path="/access-log-exports"
          element={lazyRoute(<AccessLogExportsPage />)}
        />
        <Route
          path="/nova-site-access"
          element={<Navigate to="/access" replace />}
        />
        <Route
          path="/nova-site-access/raw"
          element={lazyRoute(<NovaSiteAccessPage />)}
        />
        <Route path="/cta-access" element={lazyRoute(<CtaAccessPage />)} />
        <Route path="/results" element={lazyRoute(<ResultsPage />)} />
        <Route path="/cta-action" element={<Navigate to="/results" replace />} />
        <Route
          path="/cta-action/raw"
          element={lazyRoute(<CtaActionPage />)}
        />
        <Route path="/postbacks" element={lazyRoute(<PostbacksPage />)} />
        <Route
          path="/cta-postback"
          element={<Navigate to="/postbacks" replace />}
        />
        <Route
          path="/cta-postback/raw"
          element={lazyRoute(<CtaPostbackPage />)}
        />
        <Route
          path="/direct-access"
          element={<Navigate to="/access" replace />}
        />
        <Route
          path="/direct-access/raw"
          element={lazyRoute(<DirectAccessPage />)}
        />
        <Route
          path="/direct-postback"
          element={<Navigate to="/postbacks" replace />}
        />
        <Route
          path="/direct-postback/raw"
          element={lazyRoute(<DirectPostbackPage />)}
        />
        <Route path="/media" element={lazyRoute(<MediaPage />)} />
        <Route path="/materials" element={lazyRoute(<MaterialsListPage />)} />
        <Route
          path="/materials/:materialId"
          element={lazyRoute(<MaterialDetailPage />)}
        />
        <Route
          path="/materials/:materialId/edit"
          element={lazyRoute(<MaterialEditPage />)}
        />
        <Route path="/users" element={lazyRoute(<UsersPage />)} />
        <Route
          path="/partnership-requests"
          element={lazyRoute(<PartnershipRequestsPage />)}
        />
        <Route path="/invoices" element={lazyRoute(<InvoicesPage />)} />
        <Route path="/payments" element={lazyRoute(<PaymentsPage />)} />
        <Route
          path="/mail-deliveries"
          element={lazyRoute(<MailDeliveriesPage />)}
        />
        <Route
          path="/stop-reservations"
          element={lazyRoute(<StopReservationsPage />)}
        />
        <Route path="/spark-sync" element={lazyRoute(<SparkSyncPage />)} />
        <Route path="/settings" element={lazyRoute(<SettingsPage />)} />
        <Route path="/offers" element={lazyRoute(<OffersListPage />)} />
        <Route path="/offers/new" element={lazyRoute(<OfferCreateWizard />)} />
        <Route path="/offers/:id" element={lazyRoute(<OfferDetailPage />)} />
        <Route
          path="/offers/:id/edit"
          element={lazyRoute(<OfferEditPage />)}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
